import type { App } from 'vue'
import type { Router } from 'vue-router'
import axios from 'axios'

export interface SentryPublicConfig {
  enabled: boolean
  dsn: string
  environment: string
  tracesSampleRate: number
}

let initialized = false

function clampSampleRate(value: unknown, fallback = 0.1): number {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return fallback
  return Math.min(1, Math.max(0, parsed))
}

export function emptySentryPublicConfig(): SentryPublicConfig {
  return { enabled: false, dsn: '', environment: '', tracesSampleRate: 0.1 }
}

export function normalizeSentryPublicConfig(
  raw?: Partial<SentryPublicConfig> | null,
): SentryPublicConfig {
  if (!raw) return emptySentryPublicConfig()
  return {
    enabled: raw.enabled === true,
    dsn: String(raw.dsn ?? '').trim(),
    environment: String(raw.environment ?? '').trim(),
    tracesSampleRate: clampSampleRate(raw.tracesSampleRate, 0.1),
  }
}

export function parseSentryPublicConfig(raw?: string | Partial<SentryPublicConfig> | null): SentryPublicConfig {
  if (!raw) return emptySentryPublicConfig()
  if (typeof raw === 'object') return normalizeSentryPublicConfig(raw)
  if (!raw.trim()) return emptySentryPublicConfig()

  try {
    return normalizeSentryPublicConfig(JSON.parse(raw) as Partial<SentryPublicConfig>)
  } catch {
    return emptySentryPublicConfig()
  }
}

export function isSentryInitialized(): boolean {
  return initialized
}

export async function initSentry(
  config: SentryPublicConfig,
  options: { app: App; router?: Router },
): Promise<boolean> {
  if (!config.enabled || !config.dsn) {
    return false
  }

  const Sentry = await import('@sentry/vue')

  if (initialized) {
    await Sentry.close()
    initialized = false
  }

  const integrations = options.router
    ? [Sentry.browserTracingIntegration({ router: options.router })]
    : []

  Sentry.init({
    app: options.app,
    dsn: config.dsn,
    environment: config.environment || (import.meta.dev ? 'development' : 'production'),
    tracesSampleRate: config.tracesSampleRate,
    integrations,
    ignoreErrors: [
      /ResizeObserver loop/i,
    ],
    beforeSend(event, hint) {
      const exception = hint.originalException
      if (axios.isAxiosError(exception)) {
        event.extra = {
          ...(event.extra || {}),
          apiStatus: exception.response?.status,
          apiUrl: exception.config?.url,
          apiMethod: exception.config?.method,
          apiResponse: exception.response?.data,
        }
      }
      return event
    },
  })

  initialized = true
  return true
}

export async function captureSentryException(
  error: unknown,
  context?: Record<string, unknown>,
): Promise<void> {
  if (!initialized) return

  const Sentry = await import('@sentry/vue')
  Sentry.withScope((scope) => {
    if (context) {
      for (const [key, value] of Object.entries(context)) {
        if (key === 'shopSlug' && typeof value === 'string' && value) {
          scope.setTag('shop', value)
          continue
        }
        scope.setExtra(key, value)
      }
    }
    Sentry.captureException(error)
  })
}

export async function closeSentry(): Promise<void> {
  if (!initialized) return
  const Sentry = await import('@sentry/vue')
  await Sentry.close()
  initialized = false
}

export async function setSentryShopTag(shopSlug?: string | null): Promise<void> {
  if (!initialized) return
  const Sentry = await import('@sentry/vue')
  Sentry.setTag('shop', shopSlug?.trim() || 'platform')
}
